import React, { useState } from "react";
import { Box, Button, Chip, Stack, Typography } from "@mui/material";
import { PhoneAndroid as AppIcon } from "@mui/icons-material";
import ConfirmationDialog from "../../Common/ConfirmationDialog";
import ShopCustomerDetailsDialog from "../shop-customers/ShopCustomerDetailsDialog";
import {
    useGetShopCustomerDetailsQuery,
    useUpdateShopCustomerStatusMutation,
} from "../../../store/shopCustomers/shopCustomersApis";
import { handleDateTimeDefault } from "../../../helpers/utils";

// App login (WhatsApp OTP / password) for a Customer Master record: status, referral code, block/unblock, activity.
const AppAccountSection = ({ appAccount, onChanged }) => {
    const [showActivity, setShowActivity] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [status, setStatus] = useState(appAccount?.status);
    const [updateStatus, { isLoading }] = useUpdateShopCustomerStatusMutation();
    const { data: activity, isFetching, error } = useGetShopCustomerDetailsQuery(appAccount?.id, {
        skip: !showActivity || !appAccount?.id,
    });

    if (!appAccount) {
        return (
            <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                    App account
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Not using the MagicLock app yet. When they log in with this mobile number, the app pre-fills these
                    details and links the account here.
                </Typography>
            </Box>
        );
    }

    const blocked = status === "blocked";

    const handleConfirm = async () => {
        try {
            const next = blocked ? "active" : "blocked";
            await updateStatus({ id: appAccount.id, status: next }).unwrap();
            setStatus(next);
            onChanged?.();
        } catch (e) {
            alert(e?.data?.message || "Could not update app access");
        } finally {
            setConfirmOpen(false);
        }
    };

    return (
        <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
                App account
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: "wrap" }}>
                <Chip icon={<AppIcon />} label="Uses the app" color="info" size="small" />
                <Chip label={blocked ? "Blocked" : "Active"} color={blocked ? "error" : "success"} size="small" />
                <Chip label={`Referral code ${appAccount.referral_code}`} size="small" variant="outlined" />
            </Stack>
            <Typography variant="caption" color="text.secondary" display="block">
                Joined app: {handleDateTimeDefault(appAccount.joined_at)}
                {appAccount.last_login ? <> · Last login: {handleDateTimeDefault(appAccount.last_login)}</> : null}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                <Button size="small" variant="outlined" onClick={() => setShowActivity(true)}>
                    View app activity
                </Button>
                <Button
                    size="small"
                    variant="outlined"
                    color={blocked ? "success" : "error"}
                    disabled={isLoading}
                    onClick={() => setConfirmOpen(true)}
                >
                    {blocked ? "Unblock app access" : "Block app access"}
                </Button>
            </Stack>

            <ConfirmationDialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={handleConfirm}
                title={blocked ? "Unblock app access?" : "Block app access?"}
                subtitle={
                    blocked
                        ? "They will be able to log in and shop again."
                        : "They will be logged out of the app and cannot log in or order until unblocked. Staff records are not affected."
                }
                confirmButtonTitle={blocked ? "Yes, Unblock" : "Yes, Block"}
            />
            <ShopCustomerDetailsDialog
                open={showActivity}
                onClose={() => setShowActivity(false)}
                customer={activity}
                loading={isFetching}
                error={error?.data?.message}
            />
        </Box>
    );
};

export default AppAccountSection;
